import React, { ElementType } from 'react';
import {
  HStack,
  Icon,
  IconButton,
  useToast,
} from '@chakra-ui/react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getCompanyName, Quote } from '../utils/types';
import { BsDownload, BsShare } from 'react-icons/bs';

interface SharePDFButtonProps {
  quote: Quote | null;
}

const SharePDFButton: React.FC<SharePDFButtonProps> = ({ quote }) => {
  const toast = useToast();

  const handleGeneratePDF = async () => {
    if (!quote) return;

    const doc = new jsPDF();

    // ---------- HEADER ----------
    // "QUOTE" on top-right
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('QUOTE', 200, 20, { align: 'right' });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`# ${quote.reference_number}`, 200, 28, { align: 'right' });

    // ---------- ORG INFO ----------
    let y = 50;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(quote.organization.name, 14, y);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const orgInfo = [
      quote.organization.address,
      quote.organization.email,
      quote.organization.phone_number,
    ].filter(Boolean);

    orgInfo.forEach((line) => {
      y += 6;
      doc.text(line!, 14, y);
    });

    // ---------- CUSTOMER INFO ----------
    y += 15;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text('Quote To', 14, y);

    y += 6;
    doc.setFont('helvetica', 'bold');
    doc.text(
      `${quote?.lead?.title} ${quote?.lead?.first_name} ${quote?.lead?.second_name}`,
      14,
      y,
    );

    doc.setFont('helvetica', 'normal');
    const custInfo = [
      getCompanyName(quote.lead?.company),
      quote.lead?.phone ? `+${quote.lead.phone}` : undefined,
      quote.lead?.email,
    ].filter(Boolean);

    custInfo.forEach((line) => {
      y += 6;
      doc.text(line!, 14, y);
    });

    // ---------- DATES ----------
    let dateY = 80;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Date : ${new Date(quote.created_at).toLocaleDateString()}`,
      200,
      dateY,
      { align: 'right' },
    );
    if (quote.valid_until) {
      doc.text(
        `Expiry Date : ${new Date(quote.valid_until).toLocaleDateString()}`,
        200,
        dateY + 6,
        { align: 'right' },
      );
    }

    // ---------- ITEMS TABLE ----------
    const tableData = quote.items.map((item, idx) => [
      idx + 1,
      item?.item_name,
      item?.quantity,
      item?.unit_price,
      item?.total_price,
    ]);

    autoTable(doc, {
      startY: y + 20,
      head: [['#', 'Item & Description', 'Qty', 'Rate', 'Amount']],
      body: tableData,
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [51, 51, 51], textColor: 255 }, // black header
      columnStyles: {
        0: { halign: 'center', cellWidth: 15 },
        1: { cellWidth: 80 },
        2: { halign: 'center', cellWidth: 20 },
        3: { halign: 'right', cellWidth: 30 },
        4: { halign: 'right', cellWidth: 40 },
      },
    });

    // ---------- TOTAL ----------
    const finalY = (doc as any).lastAutoTable.finalY || y + 20;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(`Total : $${quote.total_amount.toFixed(2)}`, 200, finalY + 10, {
      align: 'right',
    });

    // ---------- NOTES ----------
    if (quote.notes) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text('Notes:', 14, finalY + 20);
      doc.setFontSize(9);
      doc.text(quote.notes, 14, finalY + 26, { maxWidth: 180 });
    }

    // ---------- FOOTER ----------
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text('Generated by Sale Radius AI CRM - www.saleradius.com', 105, 290, {
      align: 'center',
    });

    // Convert PDF to Blob
    const pdfBlob = doc.output('blob');
    const file = new File(
      [pdfBlob],
      `Quotation_${quote.reference_number}.pdf`,
      { type: 'application/pdf' },
    );

    // ---------- SHARE ----------
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Quotation ${quote.reference_number}`,
          text: `Please find attached your quotation from ${quote?.organization?.name}. Looking forward to doing business with you.`,
          files: [file],
        });
      } catch (err) {
        console.error('Share failed:', err);
        toast({
          title: 'Sharing cancelled',
          status: 'info',
          duration: 3000,
        });
      }
    } else {
      toast({
        title: 'Sharing not supported',
        description: 'Your browser does not support native share prompt.',
        status: 'warning',
        duration: 4000,
      });
    }
  };

  const handleDownloadPDF = async () => {
    if (!quote) return;

    const doc = new jsPDF();

    // ---------- HEADER ----------
    // "QUOTE" on top-right
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('QUOTE', 200, 20, { align: 'right' });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`# ${quote.reference_number}`, 200, 28, { align: 'right' });

    // ---------- ORG INFO ----------
    let y = 50;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(quote.organization.name, 14, y);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const orgInfo = [
      quote.organization.address,
      quote.organization.email,
      quote.organization.phone_number,
    ].filter(Boolean);

    orgInfo.forEach((line) => {
      y += 6;
      doc.text(line!, 14, y);
    });

    // ---------- CUSTOMER INFO ----------
    y += 15;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text('Quote To', 14, y);

    y += 6;
    doc.setFont('helvetica', 'bold');
    doc.text(
      `${quote?.lead?.title} ${quote?.lead?.first_name} ${quote?.lead?.second_name}`,
      14,
      y,
    );

    doc.setFont('helvetica', 'normal');
    const custInfo = [
      getCompanyName(quote.lead?.company),
      quote.lead?.phone ? `+${quote.lead.phone}` : undefined,
      quote.lead?.email,
    ].filter(Boolean);

    custInfo.forEach((line) => {
      y += 6;
      doc.text(line!, 14, y);
    });

    // ---------- DATES ----------
    let dateY = 80;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Date : ${new Date(quote.created_at).toLocaleDateString()}`,
      200,
      dateY,
      { align: 'right' },
    );
    if (quote.valid_until) {
      doc.text(
        `Expiry Date : ${new Date(quote.valid_until).toLocaleDateString()}`,
        200,
        dateY + 6,
        { align: 'right' },
      );
    }

    // ---------- ITEMS TABLE ----------
    const tableData = quote.items.map((item, idx) => [
      idx + 1,
      item?.item_name,
      item?.quantity,
      item?.unit_price,
      item?.total_price,
    ]);

    autoTable(doc, {
      startY: y + 20,
      head: [['#', 'Item & Description', 'Qty', 'Rate', 'Amount']],
      body: tableData,
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [51, 51, 51], textColor: 255 }, // black header
      columnStyles: {
        0: { halign: 'center', cellWidth: 15 },
        1: { cellWidth: 80 },
        2: { halign: 'center', cellWidth: 20 },
        3: { halign: 'right', cellWidth: 30 },
        4: { halign: 'right', cellWidth: 40 },
      },
    });

    // ---------- TOTAL ----------
    const finalY = (doc as any).lastAutoTable.finalY || y + 20;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(`Total : $${quote.total_amount.toFixed(2)}`, 200, finalY + 10, {
      align: 'right',
    });

    // ---------- NOTES ----------
    if (quote.notes) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text('Notes:', 14, finalY + 20);
      doc.setFontSize(9);
      doc.text(quote.notes, 14, finalY + 26, { maxWidth: 180 });
    }

    // ---------- FOOTER ----------
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text('Generated by Sale Radius AI CRM - www.saleradius.com', 105, 290, {
      align: 'center',
    });

    // Convert PDF to Blob
    doc.save(`Quotation_${quote.reference_number}.pdf`);
  };

  return (
    <HStack spacing={2}>
      <IconButton
        aria-label="Share"
        colorScheme="green"
        icon={<Icon as={BsShare as ElementType} />}
        bg="#006400"
        onClick={handleGeneratePDF}
        borderRadius="lg"
        size={'sm'}
      />

      <IconButton
        aria-label="Download"
        colorScheme="green"
        bg="#006400"
        icon={<Icon as={BsDownload as ElementType} />}
        onClick={handleDownloadPDF}
        borderRadius="lg"
        size={'sm'}
      />
    </HStack>
  );
};

export default SharePDFButton;
